import { useState } from 'react';
import { MissingResourceError, ValidationError } from '#api/errors.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useDeleteDomainMutation } from './DeleteDomainDialog.queries.ts';

type Props = {
  domainId: string;
  domainName: string;
  isVerified: boolean;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteDomainDialog({ domainId, domainName, isVerified, onCancel, onDelete }: Props) {
  const deleteDomain = useDeleteDomainMutation();
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {domainName}?</div>}
      acceptButtonText="Delete"
      onCancel={onCancel}
      onAccept={() => {
        setValidationErrors(null);
        deleteDomain.mutate(domainId, {
          onSuccess: onDelete,
          onError(error) {
            if (error instanceof MissingResourceError) {
              setValidationErrors(['Domain not found, maybe it was deleted already?']);
            } else if (error instanceof ValidationError && error.errors[0]) {
              setValidationErrors(error.errors.map((e) => e.message));
            } else {
              setValidationErrors(['There was a problem deleting the domain.  Please try again or contact support.']);
            }
          },
        });
      }}
      disableAccept={deleteDomain.isPending}
      shouldRestoreFocusViaFocusScope={true}
    >
      {isVerified ?
        <>
          <p className="p">
            This domain has been verified. If you delete it and want to use it again in the future, you will need to
            re-verify domain ownership.
          </p>
          <p className="p mb-0">
            Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{domainName}</Code>?
          </p>
        </>
      : <p className="p mb-0">
          This domain <Code className="ml-6 wrap-anywhere">{domainName}</Code> has not been verified and is not used by
          any providers. It is safe to remove.
        </p>
      }
      {Boolean(validationErrors?.length) &&
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-24' : undefined}>
            {message}
          </ErrorMessage>
        ))}
    </ConfirmDialog>
  );
}
